// Canonical ids of the built-in extension tables and their normalization
// helper. Split out of `@/components/databases` so leaf modules like
// `@/components/graph/provisioned` can normalize table ids without importing
// `databases.ts` — that would be a cycle, since `databases.ts` imports
// `provisioned.ts` for the provisioned type icons and matchers.
import type {
  CellSchemaRelationTargetDefault,
  TableDefaultExtensionsKind,
} from "@ctrl/controller-rpc";

export const TASKS_TABLE_ID = "table:task_extensions______";
export const EVENTS_TABLE_ID = "table:event_extensions_____";
export const PAGES_TABLE_ID = "table:page_extensions______";
export const PEOPLE_TABLE_ID = "table:people_extensions____";
export const CALENDAR_TABLE_ID = "table:calendar_extensions__";
export const MEMBERS_TABLE_ID = "table:0uMINCCSnYv66B9Y-kWUy";
// `Database.Id.hash "notices"` (see the controller's database_definitions.ml),
// deterministic like the members id above.
export const NOTICES_TABLE_ID = "table:KvRZYNsKZ47X9hDxF7HUb";
export const LABELS_TABLE_ID = "table:Qs7P7_AMyRb-9PZOe5IQe";
export const JOURNAL_TABLE_ID = "table:daily_notes__hSahph2e";

export const defaultTableIds: Map<string, TableDefaultExtensionsKind> = new Map(
  [
    [TASKS_TABLE_ID, "task"],
    [EVENTS_TABLE_ID, "event"],
    [PAGES_TABLE_ID, "page"],
    [PEOPLE_TABLE_ID, "people"],
    [CALENDAR_TABLE_ID, "calendar"],
    [MEMBERS_TABLE_ID, "member"],
  ]
);

/**
 * Relations to default types don't store a table id: the schema holds the
 * kind string ("task", "page", ...). This maps each kind to the extension
 * table backing it, so relation targets can be resolved in database
 * dictionaries (`relationTargetTableId` in `@/components/databases`). Its
 * keys also enumerate the default relation targets, which are never
 * dangling (the built-in tables can't be deleted — see the dangling
 * relation repair in `@/components/graph/provisioned`).
 */
export const defaultRelationTargetTableIds: Record<
  CellSchemaRelationTargetDefault,
  string
> = {
  task: TASKS_TABLE_ID,
  page: PAGES_TABLE_ID,
  event: EVENTS_TABLE_ID,
  people: PEOPLE_TABLE_ID,
  member: MEMBERS_TABLE_ID,
};

/**
 * Normalize a table ID to its canonical extension form.
 *
 * Graph query results sometimes return alternative IDs for
 * built-in databases; this maps them to the well-known extension IDs
 * so that lookups in workspaceDatabases succeed.
 */
const defaultTableIdAliases = new Map<string, string>([
  ["table:LLGtTz6w6nBMdKc2ia0WV", TASKS_TABLE_ID],
  ["table:FpCLBgXyZF38tMOo0kjO8", EVENTS_TABLE_ID],
  ["table:XxK3hrL2YjZbxIQ_f8Km", CALENDAR_TABLE_ID],
  ["table:s7MqLUIiZc0lwzI-0BV_g", PAGES_TABLE_ID],
  ["table:EqAyzpF5wypserOXudhx-", PEOPLE_TABLE_ID],
]);

export const definitiveTableId = (id: string): string =>
  defaultTableIdAliases.get(id) ?? id;
